import { useMemo, useRef } from 'react';

/**
 * Debounce a callback. Drop-in replacement for react-table v7's
 * `useAsyncDebounce`, which depended on a Babel `regeneratorRuntime` global
 * that Vite/esbuild does not provide.
 *
 * The returned function delays invoking `fn` until `wait` ms have elapsed
 * since the last call, and resolves with the result of that final call — so
 * it works both for plain setters and for async loaders (e.g. react-select's
 * `loadOptions`, which expects a promise of options).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDebounceFn<TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => TReturn,
  wait = 0
): (...args: TArgs) => Promise<Awaited<TReturn>> {
  // Keep the latest fn without resetting the timer identity on every render.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useMemo(() => {
    return (...args: TArgs): Promise<Awaited<TReturn>> =>
      new Promise((resolve, reject) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          try {
            resolve(fnRef.current(...args) as Awaited<TReturn>);
          } catch (error) {
            reject(error);
          }
        }, wait);
      });
  }, [wait]);
}

export default useDebounceFn;
